/**
 * Datos del export "Inventario valorizado": una fila por artículo con su
 * precio de venta y cada bucket de stock valorizado (cantidad × precio).
 *
 * El layout y los estilos viven en `inventarioWorkbook.ts`; acá está sólo la
 * aritmética, para poder testearla sin Excel ni DOM
 * (`exportValorizado.selfcheck.ts`). La entrada es una forma mínima y propia
 * — no `InventarioRow` — así el self-check arma filas a mano sin arrastrar el
 * cliente HTTP.
 *
 * Reglas que no son obvias:
 *  - Sin precio cargado ⇒ TODAS las celdas de plata de esa fila quedan
 *    VACÍAS (null), nunca en 0. Un 0 se suma silenciosamente y deja el
 *    valorizado corto; el vacío se ve, y la planilla lo cuenta aparte.
 *  - `Valor` de Stock total = precio × Stock total, NO la suma de las 6
 *    columnas de valor. `Stock total` incluye el bucket Cotizado, que esta
 *    planilla no exporta (PowerApps lo esconde a propósito). Hoy Cotizado
 *    está en 0 en AR y MX, pero si algún día no lo está, cada valor sigue
 *    cuadrando con su columna de cantidad en vez de desfasarse.
 *  - `Sin vender` = lo que todavía NO se vendió: Préstamo/SR + Disponible +
 *    Por ingresar. Los otros tres buckets ya están comprometidos en una venta.
 */

export type BucketsValorizables = {
  prestShow: number;
  disponible: number;
  reservado: number;
  reservadoParcial: number;
  pendienteDespacho: number;
  porIngresar: number;
};

export type FilaValorizable = {
  tipo: string;
  modelo: string;
  articulo: string;
  /** `null` = artículo sin precio cargado. */
  precioVenta: number | null;
  buckets: BucketsValorizables;
  totalStock: number;
};

export type CeldaExport = string | number | null;

export type DatosValorizado = {
  /** Filas listas para volcar en la hoja, en el orden de `COLUMNAS`. */
  filas: CeldaExport[][];
  /** Tipos presentes, ordenados — alimenta la tabla por tipo del Resumen. */
  tipos: string[];
  /** Artículos exportados sin precio cargado. */
  sinPrecio: number;
};

/**
 * Columnas de la hoja Inventario, en el orden del archivo que definió el
 * cliente: los datos del producto, y después cada estado con su cantidad y
 * su valor pegados. `kind` decide el formato numérico de la columna.
 */
export const COLUMNAS = [
  { key: 'tipo', grupo: 'Producto', titulo: 'Tipo', kind: 'texto' },
  { key: 'modelo', grupo: 'Producto', titulo: 'Modelo', kind: 'texto' },
  { key: 'articulo', grupo: 'Producto', titulo: 'Artículo', kind: 'texto' },
  { key: 'precio', grupo: 'Producto', titulo: 'Precio venta', kind: 'moneda' },
  { key: 'prestShow_c', grupo: 'Préstamo / SR', titulo: 'Cant.', kind: 'cantidad' },
  { key: 'prestShow_v', grupo: 'Préstamo / SR', titulo: 'Valor', kind: 'moneda' },
  { key: 'disponible_c', grupo: 'Disponible', titulo: 'Cant.', kind: 'cantidad' },
  { key: 'disponible_v', grupo: 'Disponible', titulo: 'Valor', kind: 'moneda' },
  { key: 'reservado_c', grupo: 'Pago pendiente', titulo: 'Cant.', kind: 'cantidad' },
  { key: 'reservado_v', grupo: 'Pago pendiente', titulo: 'Valor', kind: 'moneda' },
  { key: 'parcial_c', grupo: 'Pago parcial', titulo: 'Cant.', kind: 'cantidad' },
  { key: 'parcial_v', grupo: 'Pago parcial', titulo: 'Valor', kind: 'moneda' },
  { key: 'despacho_c', grupo: 'Pendiente de despacho', titulo: 'Cant.', kind: 'cantidad' },
  { key: 'despacho_v', grupo: 'Pendiente de despacho', titulo: 'Valor', kind: 'moneda' },
  { key: 'porIngresar_c', grupo: 'Por ingresar', titulo: 'Cant.', kind: 'cantidad' },
  { key: 'porIngresar_v', grupo: 'Por ingresar', titulo: 'Valor', kind: 'moneda' },
  { key: 'total_c', grupo: 'Stock total', titulo: 'Cant.', kind: 'cantidad' },
  { key: 'total_v', grupo: 'Stock total', titulo: 'Valor', kind: 'moneda' },
  { key: 'sinVender_v', grupo: 'Sin vender', titulo: 'Valor', kind: 'moneda' },
] as const;

export type ColumnaKey = (typeof COLUMNAS)[number]['key'];

/** Índice 1-based de una columna por su `key` — para armar fórmulas. */
export function col(key: ColumnaKey): number {
  const i = COLUMNAS.findIndex((c) => c.key === key);
  if (i < 0) throw new Error(`columna desconocida: ${key}`);
  return i + 1;
}

function round2(n: number): number {
  const r = Math.round(n * 100) / 100;
  return Object.is(r, -0) ? 0 : r;
}

export function buildDatosValorizado(filas: FilaValorizable[]): DatosValorizado {
  const out: CeldaExport[][] = [];
  const tipos = new Set<string>();
  let sinPrecio = 0;

  for (const f of filas) {
    const p = f.precioVenta;
    const tienePrecio = p != null && Number.isFinite(p) && p > 0;
    if (!tienePrecio) sinPrecio += 1;
    if (f.tipo.trim()) tipos.add(f.tipo.trim());
    const valorizar = (unidades: number): number | null =>
      tienePrecio ? round2(p * unidades) : null;

    const sinVenderUnidades =
      f.buckets.prestShow + f.buckets.disponible + f.buckets.porIngresar;

    out.push([
      f.tipo,
      f.modelo,
      f.articulo,
      tienePrecio ? p : null,
      f.buckets.prestShow,
      valorizar(f.buckets.prestShow),
      f.buckets.disponible,
      valorizar(f.buckets.disponible),
      f.buckets.reservado,
      valorizar(f.buckets.reservado),
      f.buckets.reservadoParcial,
      valorizar(f.buckets.reservadoParcial),
      f.buckets.pendienteDespacho,
      valorizar(f.buckets.pendienteDespacho),
      f.buckets.porIngresar,
      valorizar(f.buckets.porIngresar),
      f.totalStock,
      valorizar(f.totalStock),
      valorizar(sinVenderUnidades),
    ]);
  }

  return { filas: out, tipos: [...tipos].sort((a, b) => a.localeCompare(b, 'es')), sinPrecio };
}
